import { createActions, createReducer } from 'reduxsauce'
import Immutable, { type ImmutableObject } from 'seamless-immutable'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import { LoginTypes } from './LoginRedux'
import { startupPhase } from 'APP/BrownfieldBridge/startupPhase'
import { StartupState } from 'APP/Store/Startup/types'

/* ------------- Types and Action Creators ------------- */

const actionDefs = {
  startup: null,
  fullStartup: null,
  fullStartupSuccess: null,
  fullStartupFailure: ['error'],
  launchStartupSequence: null,
  setPersistedLoginId: ['persistedLoginId'],
  setPushTokenRequest: ['pushToken', 'retryCount'],
  setPushTokenSuccess: ['pushToken'],
  setPushTokenFailure: ['pushToken', 'error', 'retryCount'],
  setPushVoipTokenRequest: ['pushVoipToken', 'retryCount'],
  setPushVoipTokenSuccess: ['pushVoipToken'],
  setPushVoipTokenFailure: ['pushVoipToken', 'error', 'retryCount'],
  setNotificationPermissionAccepted: ['isAccepted'],
  setLocationPermissionAccepted: ['isAccepted'],
  setRatingThreshold: ['hideRatingModal', 'episodeThreshold'],
  rateApp: ['action'],
  setChangeEmailBannerHidden: ['userId'],
  setBusierThanUsualBannerVisible: ['isVisible'],
  setBusierThanUsualBannerHidden: ['userId', 'timestamp'],
  setAppLockToggle: ['value'],
  toggleDebugFab: null,
  toggleDebugCrumbs: null,
  triggerAppLock: null,
  activateAppLockToggle: null,
  mandatoryMfaToggleRequest: null,
  mandatoryMfaToggled: null,
  setNavigationReady: null,
  resetNavigationReady: null,
} as const satisfies Record<string, string[] | null>

type TActions = typeof actionDefs
type TPayloads = {}

const { Types, Creators } = createActions<TPayloads, TActions>(actionDefs)

type ImmutableStartupState = ImmutableObject<StartupState>

export const INITIAL_STATE: ImmutableStartupState = Immutable<StartupState>({
  fullyInitialized: false,
  navigationReady: false,
  systemError: null,
  pushToken: null,
  pushTokenError: null,
  pushTokenRetryCount: 0,
  pushVoipToken: null,
  pushVoipTokenError: null,
  pushVoipTokenRetryCount: 0,
  persistedLoginId: null,
  pushPermissionsAccepted: null,
  locationPermissionAccepted: null,
  episodeThreshold: 2,
  showDebugFab: false,
  showDebugCrumbs: false,
})

export const asUserSettings = (
  userId = 'UNSPECIFIED_USER',
  propsToStore: Partial<NonNullable<StartupState['userSettings']>[string]>
) => ({
  userSettings: {
    [userId]: propsToStore,
  },
})

export const fullStartup = (state: ImmutableStartupState) => {
  // TODO DIA-86002: startupPhase should be handled as Redux state instead of a separate global module
  // We use this approach here to avoid scope creep in this PR
  // Reset startup phase when beginning a new startup
  startupPhase.reset()
  return state.merge({ fullyInitialized: true, systemError: null })
}

export const fullStartupSuccess = (state: ImmutableStartupState) => {
  // TODO DIA-86002: startupPhase should be handled as Redux state instead of a separate global module
  // We use this approach here to avoid scope creep in this PR
  // Mark startup phase as complete
  startupPhase.complete()
  return state
}

export const fullStartupFailure = (state: ImmutableStartupState, { error }: { error: string }) => {
  // TODO DIA-86002: startupPhase should be handled as Redux state instead of a separate global module
  // We use this approach here to avoid scope creep in this PR
  // Mark startup phase as complete (even on failure)
  startupPhase.complete()

  // Only reset fullyInitialized for embedded apps
  if (isEmbeddedEnvironment()) {
    return state.merge({ systemError: error, fullyInitialized: false })
  }
  return state.merge({ systemError: error })
}

export const logout = (state: ImmutableStartupState) => {
  const sessionReset = {
    // TODO: these fields appear to be set but never read anywhere in the codebase.
    // Verify they are still needed or remove them as dead state.
    pushPermissionsAccepted: null,
    locationPermissionAccepted: null,
    persistedLoginId: null,
  }
  // Reset fullyInitialized on logout for embedded apps to allow restart
  if (isEmbeddedEnvironment()) {
    return state.merge({ ...sessionReset, fullyInitialized: false })
  }
  return state.merge(sessionReset)
}

export const setPersistedLoginId = (
  state: ImmutableStartupState,
  { persistedLoginId }: { persistedLoginId: string }
) => state.merge({ persistedLoginId })

export const setPushTokenRequest = (
  state: ImmutableStartupState,
  { pushToken }: { pushToken: string }
) => state.merge({ pushToken, pushTokenRetryCount: 0 })

export const setPushTokenSuccess = (
  state: ImmutableStartupState,
  { pushToken }: { pushToken: string }
) => state.merge({ pushToken, pushTokenError: null, pushTokenRetryCount: 0 })

export const setPushTokenFailure = (
  state: ImmutableStartupState,
  {
    pushToken,
    error = null,
    retryCount = 0,
  }: { pushToken: string; error: string | null; retryCount: number }
) => state.merge({ pushToken, pushTokenError: error, pushTokenRetryCount: retryCount })

export const setPushVoipTokenRequest = (
  state: ImmutableStartupState,
  { pushVoipToken }: { pushVoipToken: string }
) => state.merge({ pushVoipToken, pushVoipTokenRetryCount: 0 })

export const setPushVoipTokenSuccess = (
  state: ImmutableStartupState,
  { pushVoipToken }: { pushVoipToken: string }
) => state.merge({ pushVoipToken, pushVoipTokenError: null, pushVoipTokenRetryCount: 0 })

export const setPushVoipTokenFailure = (
  state: ImmutableStartupState,
  {
    pushVoipToken,
    error = null,
    retryCount = 0,
  }: { pushVoipToken: string; error: string | null; retryCount: number }
) => state.merge({ pushVoipToken, pushVoipTokenError: error, pushVoipTokenRetryCount: retryCount })

export const setNotificationPermissionAccepted = (
  state: ImmutableStartupState,
  { isAccepted }: { isAccepted: boolean }
) => state.merge({ pushPermissionsAccepted: isAccepted })

export const setLocationPermissionAccepted = (
  state: ImmutableStartupState,
  { isAccepted }: { isAccepted: boolean }
) => state.merge({ locationPermissionAccepted: isAccepted })

export const setRatingThreshold = (
  state: ImmutableStartupState,
  { hideRatingModal, episodeThreshold }: { hideRatingModal: boolean; episodeThreshold: number }
) => state.merge({ hideRatingModal, episodeThreshold })

export const toggleDebugFab = (state: ImmutableStartupState) =>
  state.merge({ showDebugFab: !state.showDebugFab })

export const toggleDebugCrumbs = (state: ImmutableStartupState) =>
  state.merge({ showDebugCrumbs: !state.showDebugCrumbs })

export const setChangeEmailBannerHidden = (
  state: ImmutableStartupState,
  { userId }: { userId: string }
) => state.merge(asUserSettings(userId, { hideChangeEmailModal: true }), { deep: true })

export const setBusierThanUsualBannerVisible = (
  state: ImmutableStartupState,
  { isVisible }: { isVisible: boolean }
) => state.merge({ showBusierThanUsualModal: isVisible })

export const setBusierThanUsualBannerHidden = (
  state: ImmutableStartupState,
  { userId, timestamp }: { userId: string; timestamp: number }
) => state.merge(asUserSettings(userId, { lastBusierThanUsualModal: timestamp }), { deep: true })

export const setAppLockToggle = (state: ImmutableStartupState, { value }: { value: boolean }) =>
  state.merge({ appLock: { isToggled: value } })

export const setNavigationReady = (state: ImmutableStartupState) =>
  state.merge({ navigationReady: true })

export const resetNavigationReady = (state: ImmutableStartupState) =>
  state.merge({ navigationReady: false })

export const setDeeplink = (state: ImmutableStartupState, { deeplink }: { deeplink: unknown }) =>
  state.merge({ deeplink })

export const clearDeeplink = (state: ImmutableStartupState) => state.merge({ deeplink: null })

export const StartupTypes = Types

export default Creators

export const reducer = createReducer<TPayloads, TActions, ImmutableStartupState>(INITIAL_STATE, {
  [Types.FULL_STARTUP]: fullStartup,
  [Types.FULL_STARTUP_SUCCESS]: fullStartupSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.FULL_STARTUP_FAILURE]: fullStartupFailure,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_PERSISTED_LOGIN_ID]: setPersistedLoginId,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_PUSH_TOKEN_REQUEST]: setPushTokenRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_PUSH_TOKEN_SUCCESS]: setPushTokenSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_PUSH_TOKEN_FAILURE]: setPushTokenFailure,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_PUSH_VOIP_TOKEN_REQUEST]: setPushVoipTokenRequest,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_PUSH_VOIP_TOKEN_SUCCESS]: setPushVoipTokenSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_PUSH_VOIP_TOKEN_FAILURE]: setPushVoipTokenFailure,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_NOTIFICATION_PERMISSION_ACCEPTED]: setNotificationPermissionAccepted,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_LOCATION_PERMISSION_ACCEPTED]: setLocationPermissionAccepted,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_RATING_THRESHOLD]: setRatingThreshold,
  [Types.TOGGLE_DEBUG_FAB]: toggleDebugFab,
  [Types.TOGGLE_DEBUG_CRUMBS]: toggleDebugCrumbs,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_CHANGE_EMAIL_BANNER_HIDDEN]: setChangeEmailBannerHidden,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_BUSIER_THAN_USUAL_BANNER_VISIBLE]: setBusierThanUsualBannerVisible,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_BUSIER_THAN_USUAL_BANNER_HIDDEN]: setBusierThanUsualBannerHidden,
  // @ts-expect-error TODO: write payload model for action & add it to TActions
  [Types.SET_APP_LOCK_TOGGLE]: setAppLockToggle,
  [Types.SET_NAVIGATION_READY]: setNavigationReady,
  [Types.RESET_NAVIGATION_READY]: resetNavigationReady,
  [LoginTypes.LOGOUT]: logout,
})
